import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { SceneElement } from "../data-structures/scene-element";
import { ElementType } from "../data-structures/element-type";

export class Profile extends THREE.Line implements SceneElement {
    public layerName?: string;
    private _hover = false;
    private _selected = false;
    private _color: THREE.Color;
    public points: THREE.Vector3[] = []; // Offset points (geometry points relative to origin)
    public unit?: string = "m";
    private _line2: Line2 | null = null;
    public chartPoints: Array<{ distance: number; elevation: number }> = [];
    private _layerVisible = true;

    constructor(geometry: THREE.BufferGeometry, material: THREE.LineBasicMaterial) {
        super(geometry, material);
        
        // Store the initial color and material
        this._color = material.color.clone();
        
        // Extract relative points from geometry (these are already relative to local origin)
        const positions = geometry.attributes.position.array;
        for (let i = 0; i < positions.length; i += 3) {
            this.points.push(new THREE.Vector3(
                positions[i],
                positions[i + 1], 
                positions[i + 2]
            ));
        }

        this._layerVisible = this.visible;
    }

    setChartPoints(points: Array<{ distance: number; elevation: number }>): void {
        this.chartPoints = points;
    }

    get elementType(): ElementType {
        return "line";
    }

    get color(): THREE.Color {
        return this._color;
    }

    set color(value: THREE.Color) {
        this._color = value;
        const material = this.material as THREE.LineBasicMaterial;
        material.color = value;
        
        // Update Line2 material color if it exists
        if (this._line2) {
            const lineMaterial = this._line2.material as LineMaterial;
            lineMaterial.color.setHex(value.getHex());
        }
    }

    get hover(): boolean {
        return this._hover;
    }

    set hover(value: boolean) {
        this._hover = value;
        // Update line appearance on hover
        const material = this.material as THREE.LineBasicMaterial;
        if (value) {
            material.linewidth = 3;
        } else {
            material.linewidth = this._selected ? 3 : 2;
        }
    }

    get selected(): boolean {
        return this._selected;
    }

    set selected(value: boolean) {
        this._selected = value;
        // Update line appearance on selection
        if (value) {
            this.showAsThickLine();
        } else {
            this.showAsRegularLine();
        }
    }

    // Method to update visibility for both original line and Line2
    updateVisibility(visible: boolean) {
        this._layerVisible = visible;

        if (!visible) {
            if (this._line2 && this.parent) {
                this.parent.remove(this._line2);
                this._line2 = null;
            }
            const material = this.material as THREE.LineBasicMaterial;
            material.transparent = false;
            material.opacity = 1;
            material.depthWrite = true;
        }

        this.applyVisibilityState();

        if (visible && this._selected) {
            this.showAsThickLine();
        }
    }

    // Add method to get color as hex string for UI
    getColorHex(): string {
        return this._color.getHexString();
    }

    // Add method to set color from hex string
    setColorFromHex(hexColor: string): void {
        this._color.setHex(parseInt(hexColor.replace("#", ""), 16));
        const material = this.material as THREE.LineBasicMaterial;
        material.color = this._color;
        
        // Update Line2 material color if it exists
        if (this._line2) {
            const lineMaterial = this._line2.material as LineMaterial;
            lineMaterial.color.setHex(this._color.getHex());
        }
    }

    getTotalDistance(): number {
        let totalDistance = 0;
        // Use world coordinates for accurate distance calculation
        const worldPoints = this.getWorldPoints();
        for (let i = 0; i < worldPoints.length - 1; i++) {
            totalDistance += worldPoints[i].distanceTo(worldPoints[i + 1]);
        }
        return totalDistance;
    }

    getMinElevation(): number {
        const worldPoints = this.getWorldPoints();
        if (worldPoints.length === 0) return 0;
        return Math.min(...worldPoints.map(p => p.z));
    }

    getMaxElevation(): number {
        const worldPoints = this.getWorldPoints();
        if (worldPoints.length === 0) return 0;
        return Math.max(...worldPoints.map(p => p.z));
    }

    private createLine2(): Line2 {
        // Create LineGeometry from relative points (geometry coordinates relative to local origin)
        const positions: number[] = [];
        this.points.forEach(point => {
            positions.push(point.x, point.y, point.z);
        });
        
        const lineGeometry = new LineGeometry();
        lineGeometry.setPositions(positions);
        
        // Create LineMaterial with thick line
        const lineMaterial = new LineMaterial({
            color: this._color.getHex(),
            linewidth: 3, // Thick line for selection (in world units)
            vertexColors: false,
            dashed: false,
            transparent: false,
            opacity: 1.0
        });
        
        // Create Line2
        const line2 = new Line2(lineGeometry, lineMaterial);
        line2.computeLineDistances();
        
        // Copy transform properties from original line (maintains world position)
        line2.position.copy(this.position);
        line2.rotation.copy(this.rotation);
        line2.scale.copy(this.scale);
        line2.visible = this._layerVisible;
        
        // Add reference to original profile for hover detection
        (line2 as any).originalProfile = this;
        
        // Set initial resolution to prevent rendering issues
        // This will be updated by the viewer's updateProfileLine2Resolution method
        lineMaterial.resolution.set(window.innerWidth || 1920, window.innerHeight || 1080);
        
        return line2;
    }

    private showAsThickLine() {
        if (!this._layerVisible) {
            return;
        }
        const material = this.material as THREE.LineBasicMaterial;
        material.transparent = true;
        material.opacity = 0;
        material.depthWrite = false;

        if (!this._line2 && this.parent) {
            this._line2 = this.createLine2();
            this.parent.add(this._line2);
        }

        this.applyVisibilityState();
    }

    private showAsRegularLine() {
        if (this._line2 && this.parent) {
            this.parent.remove(this._line2);
            this._line2 = null;
        }

        const material = this.material as THREE.LineBasicMaterial;
        material.transparent = false;
        material.opacity = 1;
        material.depthWrite = true;

        this.applyVisibilityState();
    }

    private applyVisibilityState() {
        this.visible = this._layerVisible;
        if (this._line2) {
            this._line2.visible = this._layerVisible;
        }
    }

    // Method to update Line2 resolution when needed
    updateLine2Resolution(width: number, height: number) {
        if (this._line2) {
            const lineMaterial = this._line2.material as LineMaterial;
            lineMaterial.resolution.set(width, height);
        }
    }

    // Public getter for Line2 object (for hover detection)
    get line2(): Line2 | null {
        return this._line2;
    }

    // Get all world coordinates
    getWorldPoints(): THREE.Vector3[] {
        return this.points.map(p => {
            const worldPoint = p.clone();
            this.localToWorld(worldPoint);
            return worldPoint;
        });
    }
}
